import { useState, useEffect, useCallback, useRef } from "react";
import "./notificationsbooking.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Calendar, 
  MessageSquare, 
  Send, 
  AlertCircle,
  XCircle, 
  CheckCircle, 
  Clock, 
  RefreshCw, 
  Plus, 
  Trash2, 
  ExternalLink,
  Settings,
  Phone,
  User,
  X,
  Wifi,
  WifiOff,
  QrCode,
  Zap,
  PlayCircle,
  Search,
  AlertTriangle,
  CalendarDays,
  UserX,
  History,
  Mail,
  MailCheck,
  MailX,
  CreditCard,
  CheckSquare,
  Square
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_GEST_NOTIF_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAIN_BACKEND_URL =
  process.env.REACT_APP_PARAFAR_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "";
const MAIN_API = `${MAIN_BACKEND_URL}/api`;

const NOTIF_CACHE_KEY = "famapos_notifications_cache_v1";

const readNotificationsCache = () => {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(NOTIF_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeNotificationsCache = (data) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(NOTIF_CACHE_KEY, JSON.stringify({
      ...data,
      cached_at: new Date().toISOString(),
    }));
  } catch {}
};

const getMainApiConfig = () => {
  const existingAuth =
    axios?.defaults?.headers?.common?.Authorization ||
    axios?.defaults?.headers?.Authorization;

  if (existingAuth) {
    return { headers: { Authorization: existingAuth } };
  }

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("ervapos_token");

  if (token) {
    return {
      headers: {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`
      }
    };
  }

  return {};
};

const getApiConfig = () => {
  const existingAuth =
    axios?.defaults?.headers?.common?.Authorization ||
    axios?.defaults?.headers?.Authorization;

  if (existingAuth) {
    return { headers: { Authorization: existingAuth } };
  }

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("ervapos_token");

  if (token) {
    return {
      headers: {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`
      }
    };
  }

  return {};
};

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card className="stat-card border-2 border-border" data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</p>
          <p className="text-3xl font-black mt-2 font-heading">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-sm bg-white/5 border border-white/10`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Message Type Badge
const MessageTypeBadge = ({ type, displayType }) => {
  const config = {
    "48h": { label: "48h", className: "badge-48h" },
    "24h": { label: "24h", className: "badge-24h" },
    "change": { label: "Alteração", className: "badge-change" },
    "cancellation": { label: "Cancelado", className: "badge-cancellation" },
    "expired": { label: "Expirado", className: "bg-gray-500 text-white" }
  };
  
  // Usar displayType se for "expired", senão usar type
  const typeToUse = displayType === "expired" ? "expired" : type;
  const { label, className } = config[typeToUse] || { label: type, className: "bg-gray-500 text-white" };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-sm ${className}`}>
      {label}
    </span>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: "Pendente", icon: Clock, className: "status-pending border" },
    manual_pending: { label: "Aguarda autorização", icon: AlertTriangle, className: "bg-orange-100 text-orange-800 border border-orange-300" },
    ignored: { label: "Ignorado", icon: XCircle, className: "bg-slate-100 text-slate-700 border border-slate-300" },
    queued: { label: "Na fila protegida", icon: Clock, className: "bg-blue-100 text-blue-800 border border-blue-300" },
    delivered: { label: "Entregue", icon: CheckCircle, className: "status-sent border" },
    sent: { label: "Enviado", icon: CheckCircle, className: "status-sent border" },
    failed: { label: "Falhou", icon: AlertCircle, className: "status-failed border" }
  };
  const { label, icon: Icon, className } = config[status] || config.pending;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-sm ${className}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

// Email Status Badge
const EmailStatusBadge = ({ status }) => {
  const config = {
    pending: { icon: Mail, className: "text-yellow-600", title: "Email pendente" },
    sent: { icon: MailCheck, className: "text-green-600", title: "Email enviado" },
    failed: { icon: MailX, className: "text-red-600", title: "Email falhou" },
    not_required: { icon: Mail, className: "text-gray-400", title: "Email não necessário" }
  };
  const { icon: Icon, className, title } = config[status] || config.pending;
  
  return (
    <span className={`inline-flex items-center ${className}`} title={title}>
      <Icon className="h-4 w-4" />
    </span>
  );
};

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  const weekdays = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
  const weekday = weekdays[date.getDay()];
  
  return `${weekday}, ${day}/${month}/${year}, ${hours}:${minutes}`;
};

// Available Slots Panel Component
const SlotsPanel = () => {
  const [slotsData, setSlotsData] = useState({ available: [], total_available: 0 });
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState(100);

  // Fetch specialties on mount
  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const response = await axios.get(`${API}/specialties`, getApiConfig());
        const data = Array.isArray(response?.data) ? response.data : [];
        const unique = [];
        const seen = new Set();

        for (const item of data) {
          const name = String(item?.specialty || "").trim();
          if (!name) continue;
          if (seen.has(name)) continue;
          seen.add(name);
          unique.push(item);
        }

        setSpecialties(unique);

        if (unique.some(item => String(item?.specialty || "").trim() === "Clínica Geral em Naturopatia")) {
          setSelectedSpecialty((prev) => prev === "all" ? "Clínica Geral em Naturopatia" : prev);
        }
      } catch (error) {
        console.error("Error fetching specialties:", error);
        setSpecialties([]);
      }
    };
    fetchSpecialties();
  }, []);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const specialtyParam = selectedSpecialty !== "all" ? `&specialty=${encodeURIComponent(selectedSpecialty)}` : "";
      const response = await axios.get(
        `${API}/slots/available?days_ahead=${daysAhead}${specialtyParam}`,
        getApiConfig()
      );

      const payload = response?.data && typeof response.data === "object" ? response.data : {};
      const available = Array.isArray(payload.available) ? payload.available : [];
      const total_available = Number(payload.total_available || available.length) || 0;

      setSlotsData({ available, total_available });
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlotsData({ available: [], total_available: 0 });
    } finally {
      setLoading(false);
    }
  }, [daysAhead, selectedSpecialty]);

  useEffect(() => {
    fetchSlots();

    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchSlots();
    }, 900000);

    const handleVisibilityChange = () => {
      if (!document.hidden) fetchSlots();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSlots]);

  // Open Google Calendar on specific date
  const openGoogleCalendar = (dateStr) => {
    // Format: YYYYMMDD for Google Calendar URL
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const googleCalendarUrl = `https://calendar.google.com/calendar/r/day/${year}/${month}/${day}`;
    window.open(googleCalendarUrl, '_blank');
  };


  // Group slots by date
  const slotsByDate = {};
  slotsData.available.forEach(slot => {

    if (!slotsByDate[slot.date]) {
      slotsByDate[slot.date] = { weekday: slot.weekday, slots: [] };
    }
    slotsByDate[slot.date].slots.push(slot);
  });

  // Sort slots within each day
  Object.keys(slotsByDate).forEach(date => {
    slotsByDate[date].slots.sort((a, b) => a.time.localeCompare(b.time));
  });

  return (
    <Card className="border-2 border-border" data-testid="slots-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Vagas Disponíveis
            </CardTitle>
            <CardDescription>
              Intervalos livres entre consultas (mínimo 15 min)
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
              <SelectTrigger className="w-44 h-9">
                <SelectValue placeholder="Especialidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Especialidades</SelectItem>
                {specialties.map((spec) => (
                  <SelectItem key={spec.specialty} value={spec.specialty}>
                    {spec.specialty}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(daysAhead)} onValueChange={(v) => setDaysAhead(parseInt(v))}>
              <SelectTrigger className="w-28 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="14">14 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchSlots}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Show selected specialty info */}
        {selectedSpecialty !== "all" && (
          <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-sm text-sm">
            <span className="font-medium">{selectedSpecialty}</span>
            {specialties.find(s => s.specialty === selectedSpecialty)?.professional && (
              <span className="text-muted-foreground"> - {specialties.find(s => s.specialty === selectedSpecialty)?.professional}</span>
            )}
          </div>
        )}
        
        {/* Legend */}
        <div className="flex items-center gap-4 mb-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm bg-green-100 border border-green-400"></div>
            <span>Disponível</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm bg-orange-100 border border-orange-400"></div>
            <span>Vaga de Cancelamento</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <ExternalLink className="h-3 w-3" />
            <span>Clique para abrir agenda</span>
          </div>
        </div>

        <ScrollArea className="h-[450px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : Object.keys(slotsByDate).length === 0 ? (
            <div className="empty-state py-12">
              <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-semibold text-muted-foreground">Sem vagas</p>
              <p className="text-sm text-muted-foreground">Agenda completa para os próximos {daysAhead} dias</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(slotsByDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, { weekday, slots }]) => {
                const dateObj = new Date(date);
                const formattedDate = `${weekday}, ${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
                
                return (
    <div key={date} className="nb-scope border rounded-sm p-3">
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      {formattedDate}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {slots.map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => openGoogleCalendar(slot.datetime)}
                          className={`px-3 py-1.5 rounded-sm text-sm font-medium border cursor-pointer transition-all hover:scale-105 hover:shadow-md ${
                            slot.is_cancelled_slot
                              ? 'bg-orange-100 border-orange-400 text-orange-800 hover:bg-orange-200'
                              : 'bg-green-100 border-green-400 text-green-800 hover:bg-green-200'
                          }`}
                          title={`${slot.time} - ${slot.end_time || '?'} (${slot.duration_display || '?'}) - Clique para abrir agenda`}
                        >
                          <span className="font-semibold">{slot.time}</span>
                          {slot.end_time && <span className="text-xs ml-1 opacity-70">-{slot.end_time}</span>}
                          <span className="ml-1 text-xs bg-white/50 px-1 rounded">{slot.duration_display}</span>
                          {slot.is_cancelled_slot && <span className="ml-1">⚡</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
          <p className="font-medium mb-1">Horário Dr. Mário Tinoco:</p>
          <p>• Segunda e Quarta: 14:30 - 19:00</p>
          <p>• Terça, Quinta e Sexta: 11:00 - 12:30 e 14:30 - 19:00</p>
          <p>• Mínimo de vaga: 15 minutos</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Format date only (no time)
const formatDateOnly = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("pt-PT", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

// Changes/Cancellations Panel Component
const ChangesPanel = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(50);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages?limit=10000`, getApiConfig());
      // Filter only active changes and cancellations
      const filtered = response.data.filter(m => {
        const kind = String(m.display_type || m.message_type || "").trim().toLowerCase();
        const status = String(m.status || "").trim().toLowerCase();
        return status !== "expired" && [
          "change",
          "changed",
          "alteration",
          "alteracao",
          "cancellation",
          "cancelled",
          "cancelamento"
        ].includes(kind);
      });
      setMessages(filtered);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Filter by search
  const filteredMessages = messages.filter(msg => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = msg.patient_name?.toLowerCase().includes(query);
    const phoneMatch = msg.patient_phone?.toLowerCase().replace(/\s+/g, '').includes(query.replace(/\s+/g, ''));
    return nameMatch || phoneMatch;
  });

  const handleSendAuto = async () => {
    toast.info("Envio automático direto desligado por segurança. Use Autorizações e confirme cada SMS antes do envio.");
  };

  const handleUpdateStatus = async () => {
    toast.info("Alteração manual de estado desligada por segurança neste painel.");
  };

  const openWhatsApp = (link) => {
    if (!link) {
      toast.error("Link de SMS indisponível");
      return;
    }
    window.open(link, "_blank");
  };

  return (
    <Card className="border-2 border-border" data-testid="changes-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Alterações e Cancelamentos
            </CardTitle>
            <CardDescription>Consultas alteradas ou desmarcadas</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchMessages}
            disabled={loading}
            data-testid="refresh-changes-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search and Limit */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pesquisar nome ou telefone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="search-changes-input"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Mostrar:</span>
            <Select 
              value={String(limit)} 
              onValueChange={(value) => setLimit(value === "all" ? "all" : parseInt(value))}
            >
              <SelectTrigger className="w-24 h-9" data-testid="changes-limit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {searchQuery && (
          <p className="text-sm text-muted-foreground mb-3">
            {filteredMessages.length} resultado{filteredMessages.length !== 1 ? 's' : ''} para "{searchQuery}"
          </p>
        )}

        <ScrollArea className="h-[450px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="empty-state py-12">
              <RefreshCw className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-semibold text-muted-foreground">Sem alterações</p>
              <p className="text-sm text-muted-foreground">Nenhuma consulta alterada ou cancelada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-bold">Paciente</TableHead>
                  <TableHead className="font-bold">Tipo</TableHead>
                  <TableHead className="font-bold">Data</TableHead>
                  <TableHead className="font-bold">Status</TableHead>
                  <TableHead className="font-bold text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMessages.map((msg) => (
                  <TableRow 
                    key={msg.id}
                    className={String(msg.display_type || msg.message_type || "").toLowerCase().includes("cancel") ? "bg-red-50" : "bg-purple-50"}
                    data-testid={`change-row-${msg.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-semibold text-sm">{msg.patient_name}</p>
                        <p className="text-xs text-muted-foreground">{msg.patient_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {String(msg.display_type || msg.message_type || "").toLowerCase().includes("cancel") ? (
                        <span className="px-2 py-0.5 text-xs font-bold rounded-sm bg-red-500 text-white">
                          Cancelado
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-bold rounded-sm bg-purple-500 text-white">
                          Alteração
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(msg.scheduled_for)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={msg.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {msg.status === "manual_pending" && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm bg-orange-50 text-orange-700 border border-orange-200">
                            Rever em Autorizações
                          </span>
                        )}
                        {msg.status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => openWhatsApp(msg.whatsapp_link)}
                            data-testid={`change-manual-${msg.id}`}
                            title="Abrir SMS para revisão"
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Rever SMS
                          </Button>
                        )}
                        {msg.status === "sent" && (
                          <span
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm bg-green-50 text-green-700 border border-green-200"
                            title="SMS já enviada protegida"
                          >
                            Protegida
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Appointments Panel Component
const AppointmentsPanel = ({ noShowAlerts }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all, future, past, no-show
  
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = `${API}/appointments?limit=200`;
      if (activeFilter === "future") {
        endpoint = `${API}/appointments/future?limit=200`;
      } else if (activeFilter === "past") {
        endpoint = `${API}/appointments/past?limit=200`;
      } else if (activeFilter === "no-show") {
        endpoint = `${API}/appointments/no-shows?limit=200`;
      }
      
      const response = await axios.get(endpoint, getApiConfig());
      const data = Array.isArray(response?.data) ? response.data : [];
      setAppointments(data);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Filter by search
  const filteredAppointments = appointments.filter(apt => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = apt.patient_name?.toLowerCase().includes(query);
    const phoneMatch = apt.patient_phone?.toLowerCase().replace(/\s+/g, '').includes(query.replace(/\s+/g, ''));
    return nameMatch || phoneMatch;
  });

  // Check if patient has no-show history
  const hasNoShowAlert = (phone) => {
    // Só mostra o alerta se houver faltas reais (>0)
    return getNoShowCount(phone) > 0;
  };

  const getNoShowCount = (phone) => {
    if (!phone || !noShowAlerts) return 0;
    const phoneClean = phone.replace(/[\s\+\-]/g, '').slice(-9);
    const alert = noShowAlerts.find(a =>
      a.patient_phone?.replace(/[\s\+\-]/g, '').slice(-9) === phoneClean
    );

    // backend actual: no_show_count
    // fallback: no_show_count (se existir nalguma versão antiga)
    return Number(alert?.no_show_count ?? alert?.no_show_count ?? 0) || 0;
  };


  // Normaliza telefone para comparar sempre pelos últimos 9 dígitos
  const phoneKey = (phone) => {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    return digits.length >= 9 ? digits.slice(-9) : "";
  };

  // Quantos utentes nesta lista (já filtrada) têm histórico de faltas?
  const futureNoShowCount = (() => {
      const seen = new Set();
      const now2 = new Date();

      for (const apt of filteredAppointments) {
        const k = phoneKey(apt.patient_phone);
        if (!k) continue;

        const aptDate = new Date(apt.appointment_date);
        const isPast2 = aptDate < now2;
        const isNoShow2 = !!apt.is_no_show;

        const cnt = getNoShowCount(apt.patient_phone);
        if (!isPast2 && !isNoShow2 && cnt > 0) {
          seen.add(k);
        }
      }

      return seen.size;
    })();

    const handleNoShowBannerClick = () => {
      const root = document.querySelector('[data-testid="appointments-panel"]');
      const el = root ? root.querySelector('.nb-nosh-alert-row') : document.querySelector('.nb-nosh-alert-row');

      if (!el) {
        try { toast.error("Não encontrei nenhuma consulta futura com alerta nesta lista."); } catch (e) {}
        return;
      }

      try { toast.message("A ir para a primeira consulta com histórico de faltas…"); } catch (e) {}

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('nb-flash');
      window.setTimeout(() => el.classList.remove('nb-flash'), 900);
    };


  const now = new Date();

  

    // refs para scroll até uma consulta específica
    const rowRefs = useRef({});
    const [highlightId, setHighlightId] = useState(null);

    const scrollToFirstAlert = () => {
      // procura a primeira consulta FUTURA (na lista actual) que tenha histórico
      const first = filteredAppointments.find((apt) => {
        const aptDate = new Date(apt.appointment_date);
        const isPast = aptDate < now;
        const isNoShow = !!apt.is_no_show;
        const cnt = getNoShowCount(apt.patient_phone);
        return !isPast && !isNoShow && cnt > 0;
      });

      if (!first) {
        toast.error("Não encontrei nenhuma consulta futura com histórico de faltas nesta lista.");
        return;
      }

      const el = rowRefs.current[first.id];
      if (!el) {
        toast.error("Não consegui localizar a linha no ecrã. Tenta de novo após actualizar.");
        return;
      }

      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(first.id);
      setTimeout(() => setHighlightId(null), 1800);
    };
return (
    <Card className="border-2 border-border" data-testid="appointments-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Agenda de Consultas
            </CardTitle>
            <CardDescription>Histórico e próximas consultas</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchAppointments}
            disabled={loading}
            data-testid="refresh-appointments-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Tabs and Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex gap-1 p-1 bg-muted rounded-sm">
            <Button
              size="sm"
              variant={activeFilter === "all" ? "default" : "ghost"}
              onClick={() => setActiveFilter("all")}
              className="h-8 text-xs"
              data-testid="filter-all-appointments"
            >
              Todos
            </Button>
            <Button
              size="sm"
              variant={activeFilter === "future" ? "default" : "ghost"}
              onClick={() => setActiveFilter("future")}
              className="h-8 text-xs"
              data-testid="filter-future-appointments"
            >
              Futuros
            </Button>
            <Button
              size="sm"
              variant={activeFilter === "past" ? "default" : "ghost"}
              onClick={() => setActiveFilter("past")}
              className="h-8 text-xs"
              data-testid="filter-past-appointments"
            >
              Passados
            </Button>
            <Button
              size="sm"
              variant={activeFilter === "no-show" ? "default" : "ghost"}
              onClick={() => setActiveFilter("no-show")}
              className="h-8 text-xs text-red-600"
              data-testid="filter-noshow-appointments"
            >
              <UserX className="h-3 w-3 mr-1" />
              Faltas
            </Button>
          </div>
          
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pesquisar nome ou telefone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="search-appointments-input"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {searchQuery && (
          <p className="text-sm text-muted-foreground mb-3">
            {filteredAppointments.length} resultado{filteredAppointments.length !== 1 ? 's' : ''} para "{searchQuery}"
          </p>
        )}

        {/* No-Show Alerts Banner */}
        {futureNoShowCount > 0 && activeFilter === "future" && (
          <Alert className="mb-4 cursor-pointer bg-red-500/10 border border-red-500/25" onClick={handleNoShowBannerClick} role="button" tabIndex={0}>

            <AlertTriangle className="h-4 w-4 text-red-300" />
            <AlertTitle className="text-red-200">Atenção: Pacientes com histórico de faltas</AlertTitle>
            <AlertDescription className="text-red-200/90">
              {futureNoShowCount} paciente{futureNoShowCount !== 1 ? 's' : ''} com consultas futuras já faltou anteriormente sem avisar.
            </AlertDescription>
          </Alert>
        )}

        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="empty-state py-12">
              <CalendarDays className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-semibold text-muted-foreground">Sem agendamentos</p>
              <p className="text-sm text-muted-foreground">
                {activeFilter === "no-show" ? "Nenhuma falta registada" : "Nenhum agendamento encontrado"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-bold">Paciente</TableHead>
                  <TableHead className="font-bold">Data</TableHead>
                  <TableHead className="font-bold">Especialidade</TableHead>
                  <TableHead className="font-bold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAppointments.map((apt) => {
                  const aptDate = new Date(apt.appointment_date);
                  const isPast = aptDate < now;
                  const isNoShow = apt.is_no_show;
                  const hasAlert = (hasNoShowAlert(apt.patient_phone) && getNoShowCount(apt.patient_phone) > 0) && !isPast && !isNoShow;
                  const noShowCount = getNoShowCount(apt.patient_phone);
                  
                  return (
                    <TableRow
                      key={apt.id}
                      ref={(el) => { if (el) rowRefs.current[apt.id] = el; }}
                      className={`
                        ${isNoShow ? 'bg-red-500/10 border-l-4 border-red-500/40' : ''}
                        ${hasAlert ? 'bg-amber-500/10 border-l-4 border-amber-500/40' : ''}
                        ${hasAlert ? 'nb-nosh-alert-row' : ''}
                        ${isPast && !isNoShow ? 'opacity-60' : ''}
                        ${highlightId === apt.id ? "ring-2 ring-amber-400/60" : ""}
                      `}
                      data-testid={`appointment-row-${apt.id}`}
                    >
                      <TableCell>
                        <div className="flex items-start gap-2">
                          {(isNoShow || hasAlert) && (
                            <div className="mt-0.5">
                              {isNoShow ? (
                                <UserX className="h-4 w-4 text-red-500" title="Faltou sem avisar" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-amber-300" title={`Histórico: ${noShowCount} falta(s)`} />
                              )}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-sm">{apt.patient_name}</p>
                            <p className="text-xs text-muted-foreground">{apt.patient_phone}</p>
                            {hasAlert && (
                              <p className="text-xs text-amber-200 font-medium mt-0.5">
                                ⚠️ {noShowCount} falta{noShowCount !== 1 ? 's' : ''} anterior{noShowCount !== 1 ? 'es' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(apt.appointment_date)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{apt.specialty || "-"}</p>
                          <p className="text-xs text-muted-foreground">{apt.doctor || "-"}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isNoShow ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-sm bg-red-500/15 text-red-200 border border-red-500/30">
                            <UserX className="h-3 w-3" />
                            Faltou
                          </span>
                        ) : isPast ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-sm bg-slate-500/10 text-slate-200 border border-white/10">
                            <History className="h-3 w-3" />
                            Passado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-sm bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                            <Clock className="h-3 w-3" />
                            Agendado
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// WhatsApp Connection Panel
const WhatsAppPanel = ({ onStatusChange }) => {
  const [status, setStatus] = useState({ status: 'disconnected', connected: false, user: null });
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, getApiConfig());
      setStatus(response.data);
      if (onStatusChange) onStatusChange(response.data.connected);
      return response.data;
    } catch (error) {
      console.error("WhatsApp status error:", error);
      setStatus({ status: 'error', connected: false, user: null });
      return null;
    }
  }, [onStatusChange]);

  const fetchQR = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr`, getApiConfig());
      setQrData(response.data);
    } catch (error) {
      console.error("QR fetch error:", error);
    }
  }, []);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(() => {
      checkStatus();
      if (!status.connected) {
        fetchQR();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [checkStatus, fetchQR, status.connected]);

  const handleReconnect = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/whatsapp/reconnect`, {}, getApiConfig());
      toast.info("A reconectar WhatsApp...");
    } catch (error) {
      toast.error("Erro ao reconectar");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/whatsapp/disconnect`, {}, getApiConfig());
      toast.success("WhatsApp desconectado");
      setStatus({ status: 'disconnected', connected: false, user: null });
    } catch (error) {
      toast.error("Erro ao desconectar");
    } finally {
      setLoading(false);
    }
  };

  const handleSendAllPending = async () => {
    toast.info("Envio automático em lote está desligado por segurança. Use Autorizações ou envio individual depois de rever cada SMS.");
  };

  return (
    <Card className="border-2 border-border" data-testid="whatsapp-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold font-heading flex items-center gap-2">
            {status.connected ? (
              <Wifi className="h-5 w-5 text-green-500" />
            ) : (
              <WifiOff className="h-5 w-5 text-red-500" />
            )}
            WhatsApp Auto
          </CardTitle>
          <div className={`px-2 py-1 rounded-sm text-xs font-bold ${
            status.connected ? 'bg-green-100 text-green-700' : 
            status.status === 'waiting_qr' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {status.connected ? 'Conectado' : 
             status.status === 'waiting_qr' ? 'Aguarda QR' : 
             'Desconectado'}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.connected ? (
          <>
            <Alert className="bg-emerald-500/10 border-emerald-500/30">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Conectado</AlertTitle>
              <AlertDescription className="text-green-700">
                {status.user?.name || status.user?.id || 'WhatsApp pronto para enviar'}
              </AlertDescription>
            </Alert>
            
            <Alert className="bg-orange-50 border-orange-200">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">Envio em lote desligado</AlertTitle>
              <AlertDescription className="text-orange-700">
                Por segurança, a app não envia todas as SMS pendentes de uma vez. Confirme e envie apenas as mensagens necessárias.
              </AlertDescription>
            </Alert>

            <Button 
              variant="outline"
              className="w-full text-orange-700 border-orange-300 bg-orange-50 cursor-not-allowed"
              onClick={handleSendAllPending}
              disabled={sendingAll}
              data-testid="send-all-pending-btn-disabled"
            >
              <Zap className="h-4 w-4 mr-2" />
              Envio em lote bloqueado
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleDisconnect}
              disabled={loading}
              data-testid="disconnect-whatsapp-btn"
            >
              <WifiOff className="h-4 w-4 mr-2" />
              Desconectar
            </Button>
          </>
        ) : (
          <>
            {qrData?.qrDataUrl ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  Escaneie com WhatsApp:
                </p>
                <div className="flex justify-center">
                  <img 
                    src={qrData.qrDataUrl} 
                    alt="WhatsApp QR Code" 
                    className="w-48 h-48 border-2 border-border rounded-sm"
                    data-testid="whatsapp-qr-code"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  WhatsApp → Configurações → Aparelhos conectados
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <QrCode className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground mb-3">
                  Aguardando código QR...
                </p>
                <Button 
                  variant="outline"
                  onClick={handleReconnect}
                  disabled={loading}
                  data-testid="reconnect-whatsapp-btn"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Conectar WhatsApp
                </Button>
              </div>
            )}
          </>
        )}
        
        <Separator />
        
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="flex items-center gap-1">
            <Phone className="h-3 w-3" />
            Remetente: +351 927 904 326
          </p>
          <p>• Envio automático via Baileys</p>
          <p>• Mensagens 48h e 24h antes</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Main App Component
function NotificationsBookingApp() {
  const initialCacheRef = useRef(readNotificationsCache());
  const initialCache = initialCacheRef.current;
  const [stats, setStats] = useState(initialCache?.stats || {
    total_appointments: 0,
    pending_messages: 0,
    sent_messages: 0,
    queued_messages: 0,
    failed_messages: 0,
    ignored_messages: 0,
    pending_due_now: 0,
    changes_detected: 0,
    cancellations: 0,
    no_shows: 0
  });
  const [messages, setMessages] = useState(initialCache?.messages || []);
  const [allMessages, setAllMessages] = useState(initialCache?.allMessages || []);
  const [messagesByStatus, setMessagesByStatus] = useState(initialCache?.messagesByStatus || {});
  const [calendars, setCalendars] = useState(initialCache?.calendars || []);
  const [loadingInitial, setLoadingInitial] = useState(!initialCache);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [sectionTab, setSectionTab] = useState("today");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [noShowAlerts, setNoShowAlerts] = useState(initialCache?.noShowAlerts || []);
  const [messagesLimit, setMessagesLimit] = useState(100);
  
  // Consultas prépagas
  const [prepaidAppointments, setPrepaidAppointments] = useState([]);

  // Pedidos de marcação com pré-pagamento
  const [consultationBookings, setConsultationBookings] = useState(initialCache?.consultationBookings || []);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [newBooking, setNewBooking] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    specialty: "Clínica Geral em Naturopatia",
    professional_name: "Dr. Mário Tinoco",
    requested_date: "",
    requested_time: "",
    duration_minutes: "45",
    payment_required: true,
    payment_method: "mbway",
    payment_amount: "20",
    notes: "",
    calendar_id: ""
  });
  
  // Seleção múltipla de mensagens
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Calendar form state
  const [newCalendar, setNewCalendar] = useState({ name: "", ical_url: "" });
  const [addingCalendar, setAddingCalendar] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const refreshConsultationBookings = useCallback(async ({ clearOnError = false } = {}) => {
    try {
      const bookingsRes = await axios.get(`${MAIN_API}/consultation-bookings`, getMainApiConfig());
      const bookingsData = Array.isArray(bookingsRes?.data) ? bookingsRes.data : [];
      setConsultationBookings(bookingsData);
      return bookingsData;
    } catch (bookingError) {
      console.error("Error fetching consultation bookings:", bookingError);
      if (clearOnError) {
        setConsultationBookings([]);
      }
      return null;
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async (isBackground = false) => {
    if (isBackground) {
      setRefreshing(true);
    } else if (!initialCache) {
      setLoadingInitial(true);
    }

    try {
      if (!isBackground) {
        const [calendarsRes, alertsRes] = await Promise.all([
          axios.get(`${API}/calendars`, getApiConfig()).catch(() => ({ data: [] })),
          axios.get(`${API}/alerts/no-show-patients`, getApiConfig()).catch(() => ({ data: [] })),
        ]);

        setCalendars(calendarsRes.data || []);
        setNoShowAlerts(alertsRes.data || []);
        setPrepaidAppointments([]);

        await refreshConsultationBookings({ clearOnError: !initialCache });
      }

      const requestLimit = messagesLimit === "all" ? 500 : Math.max(25, Number(messagesLimit || 100));
      const messagesUrl = activeTab === "all"
        ? `${API}/messages?limit=${requestLimit}`
        : `${API}/messages?status=${activeTab}&limit=${requestLimit}`;

      const [messagesRes, statsRes] = await Promise.all([
        axios.get(messagesUrl, getApiConfig()).catch(() => ({ data: [] })),
        axios.get(`${API}/stats`, getApiConfig()).catch(() => ({ data: null })),
      ]);

      const messagesData = Array.isArray(messagesRes?.data) ? messagesRes.data : [];

      setMessages(messagesData);
      setMessagesByStatus((prev) => ({
        ...(prev || {}),
        [activeTab]: messagesData,
      }));

      if (activeTab === "all") {
        setAllMessages(messagesData);
      }

      const baseStats = statsRes?.data || {
        total_appointments: 0,
        pending_messages: 0,
        sent_messages: 0,
        queued_messages: 0,
        failed_messages: 0,
        ignored_messages: 0,
        pending_due_now: 0,
        changes_detected: 0,
        cancellations: 0,
        no_shows: 0,
        manual_authorizations: 0
      };

      setStats(baseStats);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoadingInitial(false);
      setRefreshing(false);
    }
  }, [activeTab, messagesLimit, initialCache, refreshConsultationBookings]);
  // Manual refresh with toast feedback
  const handleRefresh = async () => {
    await fetchData(true);
    setSelectedMessages(new Set()); // Limpar seleção ao atualizar
    toast.success("Dados atualizados!");
  };

  // Toggle selection of a message
  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Select/deselect all visible messages
  const toggleSelectAll = (filteredMessages) => {
    if (selectedMessages.size === filteredMessages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(filteredMessages.map(m => m.id)));
    }
  };

  // Bulk action handler seguro
  const handleBulkAction = async (action) => {
    if (selectedMessages.size === 0) {
      toast.error("Selecione pelo menos uma mensagem");
      return;
    }

    const selectedArray = Array.from(selectedMessages);
    const source = messagesSource || messages || [];
    const selectedCurrentMessages = selectedArray
      .map((id) => source.find((m) => m.id === id) || messages.find((m) => m.id === id))
      .filter(Boolean);

    if (action === "open_whatsapp") {
      const first = selectedCurrentMessages[0];
      if (!first) {
        toast.error("Mensagem não encontrada");
        return;
      }

      const link = first.whatsapp_link;
      if (link) {
        window.open(link, "_blank");
        return;
      }

      const cleanPhone = String(first.patient_phone || "").replace(/\D/g, "");
      if (!cleanPhone) {
        toast.error("Telefone indisponível");
        return;
      }

      const text = encodeURIComponent(first.message_text || "");
      window.open(`https://wa.me/${cleanPhone}?text=${text}`, "_blank");
      return;
    }

    if (action === "ignore") {
      const manualOnly = selectedCurrentMessages.filter(
        (m) => String(m.status || "").toLowerCase() === "manual_pending"
      );

      if (manualOnly.length === 0) {
        toast.error("Só pode ignorar mensagens no separador Autorizações");
        return;
      }

      const ok = window.confirm(
        `Vai ignorar ${manualOnly.length} SMS em autorização. Confirma?`
      );
      if (!ok) return;

      setBulkActionLoading(true);
      let successCount = 0;
      let failCount = 0;

      try {
        for (const msg of manualOnly) {
          try {
            await axios.put(`${API}/messages/${msg.id}/status`, { status: "ignored" }, getApiConfig());
            successCount++;
          } catch {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`Ignoradas ${successCount} mensagem(s)`);
          await fetchData(true);
          setSelectedMessages(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} mensagem(s) falharam`);
        }
      } finally {
        setBulkActionLoading(false);
      }
      return;
    }

    toast.info("Esta ação está desligada por segurança.");
  };


  const getSelectedMessagesFromCurrentView = () => {
    const source = messagesSource || messages || [];
    return Array.from(selectedMessages)
      .map((id) => source.find((m) => m.id === id) || messages.find((m) => m.id === id))
      .filter(Boolean);
  };

  const handleSendSelectedManual = async () => {
    if (selectedMessages.size === 0) {
      toast.error("Selecione pelo menos uma mensagem para autorizar");
      return;
    }

    const allowedManualTypes = ["change", "cancellation", "asap", "24h", "48h"];
    const allowedManualStatuses = ["manual_pending"];

    const selectedManualMessages = getSelectedMessagesFromCurrentView()
      .filter((m) => {
        const kind = String(m?.message_type || m?.display_type || "").toLowerCase();
        const status = String(m?.status || "").toLowerCase();
        return m && allowedManualStatuses.includes(status) && allowedManualTypes.includes(kind);
      });

    if (selectedManualMessages.length === 0) {
      toast.error("Selecione apenas SMS no separador Autorizações.");
      return;
    }

    const ok = window.confirm(
      `Vai enviar manualmente ${selectedManualMessages.length} SMS em Autorizações. Confirma depois de rever cuidadosamente os textos e destinatários?`
    );
    if (!ok) return;

    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/messages/send-selected-manual`,
        { message_ids: selectedManualMessages.map((m) => m.id) },
        getApiConfig()
      );

      const sent = Number(response?.data?.sent || 0);
      const failed = Number(response?.data?.failed || 0);
      const blocked = Number(response?.data?.blocked || 0);

      if (sent > 0) {
        toast.success(`SMS enviadas manualmente: ${sent}`);
      }
      if (failed > 0 || blocked > 0) {
        toast.warning(`Falhadas: ${failed} | Bloqueadas: ${blocked}`);
      }
      if (sent === 0 && failed === 0 && blocked === 0) {
        toast.info("Nenhuma SMS foi enviada");
      }

      await fetchData(true);
      setSelectedMessages(new Set());
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao autorizar envio manual");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Confirm prepaid appointment
  const handleConfirmPrepaid = async (appointmentId, confirmed) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/prepaid-confirm?confirmed=${confirmed}`, {}, getApiConfig());
      setPrepaidAppointments(prev => 
        prev.map(apt => 
          apt.id === appointmentId ? {...apt, prepaid_confirmed: confirmed} : apt
        )
      );
      toast.success(confirmed ? "Confirmado como pré-pago" : "Confirmação removida");
    } catch (error) {
      toast.error("Erro ao confirmar");
    }
  };

  const handleCreatePendingEvent = async (requestId) => {
    try {
      const res = await axios.post(`${MAIN_API}/consultation-bookings/${requestId}/create-pending-event`, {}, getMainApiConfig());
      toast.success(res.data?.message || "Evento pendente criado");
      await refreshConsultationBookings();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao criar evento pendente");
    }
  };

  const handleCreateConsultationPayment = async (requestId) => {
    try {
      const res = await axios.post(`${MAIN_API}/consultation-bookings/${requestId}/create-payment`, {}, getMainApiConfig());
      const method = res?.data?.payment_method || "";
      if (method === "multibanco") {
        toast.success("Referência Multibanco gerada");
      } else if (method === "mbway") {
        toast.success("Pedido MB WAY criado");
      } else {
        toast.success(res.data?.message || "Pagamento criado");
      }
      await refreshConsultationBookings();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao criar pagamento");
    }
  };

  const handleScheduleConsultationBooking = async (requestId) => {
    try {
      const res = await axios.post(`${MAIN_API}/consultation-bookings/${requestId}/schedule`, {}, getMainApiConfig());
      toast.success(res.data?.message || "Consulta agendada");
      await refreshConsultationBookings();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao agendar");
    }
  };

  const handleCreateConsultationBooking = async () => {
    if (!newBooking.customer_name.trim()) {
      toast.error("Preencha o nome do utente");
      return;
    }
    if (!newBooking.customer_phone.trim()) {
      toast.error("Preencha o telefone");
      return;
    }
    if (!newBooking.requested_date) {
      toast.error("Preencha a data");
      return;
    }
    if (!newBooking.requested_time) {
      toast.error("Preencha a hora");
      return;
    }

    const selectedCalendarId =
      newBooking.calendar_id ||
      calendars.find(c => (c.name || "").toLowerCase().includes((newBooking.specialty || "").toLowerCase()))?.id ||
      calendars[0]?.id ||
      "";

    if (!selectedCalendarId) {
      toast.error("Não encontrei um calendário para este pedido");
      return;
    }

    const startIso = new Date(`${newBooking.requested_date}T${newBooking.requested_time}:00`).toISOString();
    const endDate = new Date(`${newBooking.requested_date}T${newBooking.requested_time}:00`);
    endDate.setMinutes(endDate.getMinutes() + parseInt(newBooking.duration_minutes || "45", 10));
    const endIso = endDate.toISOString();

    const payload = {
      customer_name: newBooking.customer_name.trim(),
      customer_phone: newBooking.customer_phone.trim(),
      customer_email: newBooking.customer_email.trim(),
      specialty: newBooking.specialty.trim(),
      professional_name: newBooking.professional_name.trim(),
      requested_date: startIso,
      requested_end: endIso,
      duration_minutes: parseInt(newBooking.duration_minutes || "45", 10),
      payment_required: !!newBooking.payment_required,
      payment_method: newBooking.payment_method,
      payment_amount: Number(newBooking.payment_amount || 0),
      notes: newBooking.notes.trim(),
      calendar_id: selectedCalendarId
    };

    setCreatingBooking(true);
    try {
      const res = await axios.post(
        `${MAIN_API}/consultation-bookings`,
        payload,
        getMainApiConfig()
      );
      toast.success(res.data?.request_number ? `Pedido criado: ${res.data.request_number}` : "Pedido criado com sucesso");
      setBookingDialogOpen(false);
      setNewBooking({
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        specialty: "Clínica Geral em Naturopatia",
        professional_name: "Dr. Mário Tinoco",
        requested_date: "",
        requested_time: "",
        duration_minutes: "45",
        payment_required: true,
        payment_method: "mbway",
        payment_amount: "20",
        notes: "",
        calendar_id: ""
      });
      await refreshConsultationBookings();
    } catch (error) {
      console.error("Erro ao criar pedido:", error);
      toast.error(error?.response?.data?.detail || "Erro ao criar pedido");
    } finally {
      setCreatingBooking(false);
    }
  };

  useEffect(() => {
    fetchData(false);

    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchData(true);
    }, 600000);

    const handleVisibilityChange = () => {
      if (!document.hidden) fetchData(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData]);

  useEffect(() => {
    const hasUsefulData =
      (allMessages || []).length > 0 ||
      (calendars || []).length > 0 ||
      (consultationBookings || []).length > 0 ||
      Number(stats?.total_appointments || 0) > 0 ||
      Number(stats?.pending_messages || 0) > 0 ||
      Number(stats?.sent_messages || 0) > 0 ||
      Number(stats?.queued_messages || 0) > 0;

    if (!hasUsefulData) return;

    writeNotificationsCache({
      stats,
      messages,
      allMessages,
      messagesByStatus,
      calendars,
      noShowAlerts,
      consultationBookings,
    });
  }, [stats, messages, allMessages, messagesByStatus, calendars, noShowAlerts, consultationBookings]);

  // Add calendar
  const handleAddCalendar = async () => {
    if (!newCalendar.name || !newCalendar.ical_url) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setAddingCalendar(true);
    try {
      await axios.post(`${API}/calendars`, newCalendar, getApiConfig());
      toast.success("Calendário adicionado com sucesso!");
      setNewCalendar({ name: "", ical_url: "" });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error adding calendar:", error);
      toast.error(error.response?.data?.detail || "Erro ao adicionar calendário");
    } finally {
      setAddingCalendar(false);
    }
  };

  // Delete calendar
  const handleDeleteCalendar = async (calendarId, calendarName = "") => {
    const ok = window.confirm(
      `Tem a certeza que pretende apagar o calendário "${calendarName || "sem nome"}"?\n\nEsta ação remove o URL iCal guardado.`
    );
    if (!ok) return;

    const ok2 = window.confirm("Confirma novamente a remoção deste calendário?");
    if (!ok2) return;

    try {
      await axios.delete(`${API}/calendars/${calendarId}`, getApiConfig());
      toast.success("Calendário removido");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover calendário");
    }
  };

  // Sync calendar
  const handleSyncCalendar = async (calendarId) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/calendars/${calendarId}/sync`, {}, getApiConfig());
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  // Sync global e relatório semanal ficam desativados até existirem rotas oficiais revistas no backend.
  // As alterações de estado manuais e reenvios automáticos diretos foram removidos da UI principal por segurança.

  // Open WhatsApp Web (manual fallback)
  const openWhatsApp = (link) => {
    if (!link) {
      toast.error("Link de SMS indisponível");
      return;
    }
    window.open(link, "_blank");
  };

  // Filter messages by status and search query
  // Quando o separador não é "Todos", a API já devolve apenas esse estado em `messages`.
  // Antes filtrava sempre `allMessages`, o que deixava Autorizações vazio apesar de existirem manual_pending na BD.
  const messagesSource = activeTab === "all"
    ? allMessages
    : (messagesByStatus?.[activeTab] || messages);

  const filteredMessagesBase = messagesSource.filter(msg => {
    if (activeTab !== "all" && msg.status !== activeTab) return false;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const nameMatch = msg.patient_name?.toLowerCase().includes(query);
      const phoneMatch = msg.patient_phone?.toLowerCase().replace(/\s+/g, '').includes(query.replace(/\s+/g, ''));
      return nameMatch || phoneMatch;
    }

    return true;
  });

  const filteredMessages =
    messagesLimit === "all"
      ? filteredMessagesBase
      : filteredMessagesBase.slice(0, Number(messagesLimit));

  if (loadingInitial) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground font-medium">A carregar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nb-scope min-h-screen bg-background" data-testid="app-container">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        
        <Alert className={`mb-4 ${stats.auto_send_enabled ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <CheckCircle className={`h-4 w-4 ${stats.auto_send_enabled ? "text-emerald-700" : "text-amber-700"}`} />
          <AlertTitle className={stats.auto_send_enabled ? "text-emerald-800" : "text-amber-800"}>
            Envio automático {stats.auto_send_enabled ? "ativo" : "inativo"}
          </AlertTitle>
          <AlertDescription className={stats.auto_send_enabled ? "text-emerald-700" : "text-amber-700"}>
            Lembretes 48h → 24h → assim que possível. Processamento a cada 5 minutos, das 08:00 às 20:00.
            Hoje: {Number(stats.sms_sent_today || 0)} de {Number(stats.sms_daily_limit || 0)} SMS. Alterações e cancelamentos exigem revisão humana.
          </AlertDescription>
        </Alert>

<div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6" data-testid="stats-grid">
          <StatCard
            title="Consultas hoje"
            value={stats.appointments_today || 0}
            icon={Calendar}
            color="bg-primary text-primary-foreground"
          />
          <StatCard
            title="Pendentes"
            value={stats.pending_messages}
            icon={Clock}
            color="bg-[#FFF5B6] text-primary"
            subtitle={`${Number(stats.pending_due_now || 0)} prontas agora`}
          />
          <StatCard
            title="Aceites hoje"
            value={stats.sms_sent_today || 0}
            icon={CheckCircle}
            color="bg-green-100 text-green-700"
          />
          <StatCard
            title="Falhados"
            value={stats.failed_messages}
            icon={AlertCircle}
            color="bg-red-100 text-red-700"
          />
          <StatCard
            title="Requer decisão"
            value={stats.manual_authorizations || 0}
            icon={AlertTriangle}
            color="bg-orange-100 text-orange-700"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="notifications-sections">
          <Button size="sm" variant={sectionTab === "today" ? "default" : "outline"} onClick={() => setSectionTab("today")}>
            Hoje
          </Button>
          <Button
            size="sm"
            variant={sectionTab === "messages" ? "default" : "outline"}
            onClick={() => setSectionTab("messages")}
          >
            Mensagens
          </Button>
          <Button
            size="sm"
            variant={sectionTab === "agenda" ? "default" : "outline"}
            onClick={() => setSectionTab("agenda")}
          >
            Agenda e Vagas
          </Button>
          <Button
            size="sm"
            variant={sectionTab === "admin" ? "default" : "outline"}
            onClick={() => setSectionTab("admin")}
          >
            Configuração e Pedidos
          </Button>
        </div>

        {/* Main Content Grid */}
        <div className="space-y-6">
          {sectionTab === "today" && (
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-2 border-border">
                <CardHeader><CardTitle className="text-lg">Operação de hoje</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="rounded-md bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Consultas</p><p className="text-2xl font-bold">{Number(stats.appointments_today || 0)}</p></div>
                  <div className="rounded-md bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Aceites pelo gateway</p><p className="text-2xl font-bold text-blue-600">{Number(stats.sms_sent_today || 0)}</p></div>
                  <div className="rounded-md bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Prontas agora</p><p className="text-2xl font-bold text-amber-600">{Number(stats.pending_due_now || 0)}</p></div>
                  <div className="rounded-md bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Falhas recentes</p><p className="text-2xl font-bold text-red-600">{Number(stats.failed_messages || 0)}</p></div>
                </CardContent>
              </Card>
              <Card className="border-2 border-border">
                <CardHeader><CardTitle className="text-lg">Ações necessárias</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <button type="button" onClick={() => { setSectionTab("messages"); setActiveTab("manual_pending"); }} className="w-full text-left rounded-md border p-4 hover:bg-muted/50">
                    <p className="font-semibold">Rever alterações e cancelamentos</p><p className="text-sm text-muted-foreground">{Number(stats.manual_authorizations || 0)} decisões recentes pendentes</p>
                  </button>
                  <button type="button" onClick={() => { setSectionTab("messages"); setActiveTab("failed"); }} className="w-full text-left rounded-md border p-4 hover:bg-muted/50">
                    <p className="font-semibold">Resolver falhas de envio</p><p className="text-sm text-muted-foreground">{Number(stats.failed_messages || 0)} falhas nos últimos 30 dias</p>
                  </button>
                </CardContent>
              </Card>
            </div>
          )}
          {sectionTab === "messages" && <div>
          {/* Messages Panel */}
          <div className="w-full">
            <Card className="border-2 border-border" data-testid="messages-panel">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Mensagens
                    </CardTitle>
                    <CardDescription>Gerir lembretes de consultas</CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loadingInitial || refreshing}
                    data-testid="refresh-messages-btn"
                  >
                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <TabsList className="grid w-full sm:w-auto grid-cols-7">
                      <TabsTrigger value="all" data-testid="tab-all">
                        Todos ({Number(stats.pending_messages || 0) + Number(stats.manual_authorizations || 0) + Number(stats.queued_messages || 0) + Number(stats.sent_messages || 0) + Number(stats.failed_messages || 0) + Number(stats.ignored_messages || 0)})
                      </TabsTrigger>
                      <TabsTrigger value="pending" data-testid="tab-pending">
                        Pendentes ({stats.pending_messages})
                      </TabsTrigger>
                      <TabsTrigger value="manual_pending" data-testid="tab-manual-pending">
                        Autorizações ({stats.manual_authorizations || 0})
                      </TabsTrigger>
                      <TabsTrigger value="sent" data-testid="tab-sent">
                        Enviados ({stats.sent_messages})
                      </TabsTrigger>
                      <TabsTrigger value="queued" data-testid="tab-queued">
                        Na fila ({stats.queued_messages || 0})
                      </TabsTrigger>
                      <TabsTrigger value="failed" data-testid="tab-failed">
                        Falhados ({stats.failed_messages})
                      </TabsTrigger>
                      <TabsTrigger value="ignored" data-testid="tab-ignored">
                        Ignoradas ({stats.ignored_messages || 0})
                      </TabsTrigger>
                    </TabsList>
                    
                    <div className="relative flex-1 max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Pesquisar nome ou telefone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9"
                        data-testid="search-input"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setSearchQuery("")}
                          data-testid="clear-search-btn"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Mostrar:</span>
                      <Select 
                        value={String(messagesLimit)} 
                        onValueChange={(value) => setMessagesLimit(value === "all" ? "all" : parseInt(value))}
                      >
                        <SelectTrigger className="w-24 h-9" data-testid="messages-limit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                          <SelectItem value="200">200</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {activeTab === "manual_pending" && (
                    <Alert className="mb-3 bg-orange-50 border-orange-200">
                      <AlertTriangle className="h-4 w-4 text-orange-700" />
                      <AlertTitle className="text-orange-800">Autorização manual obrigatória</AlertTitle>
                      <AlertDescription className="text-orange-700">
                        Aqui ficam SMS que exigem revisão humana antes do envio. Só são enviadas depois de selecionar manualmente e confirmar o envio.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Barra de ações em massa */}
                  {selectedMessages.size > 0 && (
                    <div className="flex items-center gap-2 p-2 mb-3 bg-slate-800 border border-slate-600 rounded-sm shadow-sm">
                      <span className="text-sm font-semibold text-white">
                        {selectedMessages.size} selecionado(s)
                      </span>
                      <div className="flex gap-1 ml-auto">
                        {activeTab === "manual_pending" && (
                          <Button
                            size="sm"
                            className="bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs"
                            onClick={handleSendSelectedManual}
                            disabled={bulkActionLoading}
                            data-testid="authorize-send-selected-manual-btn"
                          >
                            <CheckSquare className="h-3 w-3 mr-1" />
                            Enviar SMS selecionadas
                          </Button>
                        )}
                        {activeTab !== "manual_pending" && (
                          <>
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-slate-700 hover:bg-slate-600 text-white border border-slate-500"
                              onClick={() => handleBulkAction("open_whatsapp")}
                              disabled={bulkActionLoading}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Abrir Web
                            </Button>
                          </>
                        )}
                        {activeTab === "manual_pending" && (
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-slate-600 hover:bg-slate-500 text-white border border-slate-400"
                            onClick={() => handleBulkAction("ignore")}
                            disabled={bulkActionLoading}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Ignorar selecionadas
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => setSelectedMessages(new Set())}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {searchQuery && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {filteredMessages.length} resultado{filteredMessages.length !== 1 ? 's' : ''} para "{searchQuery}"
                    </p>
                  )}

                  <ScrollArea className="h-[500px]">
                    {filteredMessages.length === 0 ? (
                      <div className="empty-state py-12" data-testid="empty-state">
                        <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
                        <p className="text-lg font-semibold text-muted-foreground">Sem mensagens</p>
                        <p className="text-sm text-muted-foreground">Adicione um calendário para começar</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-10">
                              <Checkbox 
                                checked={selectedMessages.size === filteredMessages.length && filteredMessages.length > 0}
                                onCheckedChange={() => toggleSelectAll(filteredMessages)}
                                aria-label="Selecionar todos"
                              />
                            </TableHead>
                            <TableHead className="font-bold">Paciente</TableHead>
                            <TableHead className="font-bold">Tipo</TableHead>
                            <TableHead className="font-bold">Consulta</TableHead>
                            <TableHead className="font-bold">SMS</TableHead>
                            <TableHead className="font-bold">Email</TableHead>
                            <TableHead className="font-bold text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredMessages.map((message) => (
                            <TableRow 
                              key={message.id} 
                              className={`message-row cursor-pointer ${selectedMessages.has(message.id) ? 'bg-blue-50' : ''}`}
                              onClick={() => setSelectedMessage(message)}
                              data-testid={`message-row-${message.id}`}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox 
                                  checked={selectedMessages.has(message.id)}
                                  onCheckedChange={() => toggleMessageSelection(message.id)}
                                  aria-label={`Selecionar ${message.patient_name}`}
                                />
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-semibold text-sm">{message.patient_name}</p>
                                  <p className="text-xs text-muted-foreground">{message.patient_phone}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <MessageTypeBadge
                                  type={message.message_type}
                                  displayType={
                                    message.status === "sent" && String(message.display_type || "").toLowerCase() === "expired"
                                      ? (message.message_type || "sent")
                                      : message.display_type
                                  }
                                />
                              </TableCell>
                              <TableCell className="text-sm">
                                {formatDate(message.appointment_date || message.scheduled_for)}
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={message.status} />
                              </TableCell>
                              <TableCell>
                                <EmailStatusBadge status={message.email_status || 'pending'} />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {message.status === "manual_pending" && (
                                    <Button
                                      size="sm"
                                      className="bg-orange-600 hover:bg-orange-700 text-white h-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedMessages(new Set([message.id]));
                                        setActiveTab("manual_pending");
                                      }}
                                      data-testid={`select-manual-btn-${message.id}`}
                                      title="Selecionar para autorização manual"
                                    >
                                      <CheckSquare className="h-3 w-3 mr-1" />
                                      Autorizar
                                    </Button>
                                  )}
                                  {message.status === "pending" && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openWhatsApp(message.whatsapp_link);
                                        }}
                                        data-testid={`whatsapp-btn-${message.id}`}
                                        title="Abrir SMS Web"
                                      >
                                        <Send className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                  {message.status === "sent" && (
                                    <span
                                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm bg-green-50 text-green-700 border border-green-200"
                                      title="SMS já enviada protegida"
                                    >
                                      Protegida
                                    </span>
                                  )}
                                  {message.status === "failed" && (
                                    <span
                                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm bg-red-50 text-red-700 border border-red-200"
                                      title="Falha protegida"
                                    >
                                      Rever falha
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </ScrollArea>
                </Tabs>
              </CardContent>
            </Card>

          </div>
          </div>}

          {sectionTab === "agenda" && <div className="mt-6 space-y-6">
            <AppointmentsPanel noShowAlerts={noShowAlerts} />
            <SlotsPanel />
          </div>}

          {sectionTab === "admin" && <div>
          {/* Sidebar */}
          <div className="space-y-6">
            {/* WhatsApp Panel removido (modo SMS) */}

            <Card className="border-2 border-border" data-testid="consultation-bookings-panel">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg font-bold font-heading flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      Pedidos de Marcação
                    </CardTitle>
                    <CardDescription>Fluxo de pré-pagamento e confirmação da consulta</CardDescription>
                  </div>

                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setBookingDialogOpen(true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Novo pedido
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {consultationBookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Sem pedidos de marcação
                  </p>
                ) : (
                  <ScrollArea className="h-[420px]">
                    <div className="space-y-3">
                      {["waiting_payment", "ready_to_schedule", "scheduled"].map((section) => {
                        const items = consultationBookings.filter(item => (item.status || "") === section);
                        if (items.length === 0) return null;

                        const titleMap = {
                          waiting_payment: "A aguardar pagamento",
                          ready_to_schedule: "Pronto a agendar",
                          scheduled: "Agendado"
                        };

                        const toneMap = {
                          waiting_payment: "bg-amber-500/10 border-amber-400/40 text-amber-50",
                          ready_to_schedule: "bg-blue-500/10 border-blue-400/40 text-blue-50",
                          scheduled: "bg-emerald-500/10 border-emerald-400/40 text-emerald-50"
                        };

                        return (
                          <div key={section} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold">{titleMap[section]}</p>
                              <span className="text-xs text-muted-foreground">{items.length}</span>
                            </div>

                            {items.map((item) => (
                              <div
                                key={item.id}
                                className={`px-3 py-2 rounded-sm border shadow-sm ${toneMap[section]}`}
                              >
                                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground/90 leading-tight">
                                      <span className="font-semibold text-sm text-foreground truncate">
                                        {item.customer_name || "-"}
                                      </span>
                                      <span>{item.customer_phone || "-"}</span>
                                      <span>{formatDate(item.requested_date)}</span>
                                      <span>{item.specialty || "-"}</span>
                                      {item.professional_name ? <span>{item.professional_name}</span> : null}
                                      <span>Pedido: {item.request_number || "-"}</span>
                                      <span>Pagamento: {item.payment_method || "-"} · {item.payment_status || "-"}</span>
                                      {(item.payment_reference_entity || item.payment_reference) ? (
                                        <span>
                                          Ref.: {item.payment_reference_entity ? `${item.payment_reference_entity} ` : ""}{item.payment_reference || ""}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap gap-2 xl:justify-end">
                                    {!item.google_event_id && section === "waiting_payment" && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs"
                                        onClick={() => handleCreatePendingEvent(item.id)}
                                      >
                                        Criar pendente
                                      </Button>
                                    )}

                                    {section === "waiting_payment" && (
                                      <Button
                                        size="sm"
                                        className="h-8 text-xs"
                                        onClick={() => handleCreateConsultationPayment(item.id)}
                                      >
                                        Gerar pagamento
                                      </Button>
                                    )}

                                    {section === "ready_to_schedule" && (
                                      <Button
                                        size="sm"
                                        className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={() => handleScheduleConsultationBooking(item.id)}
                                      >
                                        Agendar
                                      </Button>
                                    )}

                                    {item.google_event_html_link && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs"
                                        onClick={() => window.open(item.google_event_html_link, "_blank")}
                                      >
                                        <ExternalLink className="h-3 w-3 mr-1" />
                                        Evento
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

{/* Prepaid Appointments Panel */}
            <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Novo pedido de marcação</DialogTitle>
                  <DialogDescription>Crie um pedido com pré-pagamento para depois gerar pagamento, criar evento pendente ou agendar.</DialogDescription>
                </DialogHeader>

                <div className="grid md:grid-cols-2 gap-4 py-2">
                  <div className="space-y-2">
                    <Label>Nome do utente</Label>
                    <Input
                      value={newBooking.customer_name}
                      onChange={(e) => setNewBooking({ ...newBooking, customer_name: e.target.value })}
                      placeholder="Nome completo"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input
                      value={newBooking.customer_phone}
                      onChange={(e) => setNewBooking({ ...newBooking, customer_phone: e.target.value })}
                      placeholder="+3519..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={newBooking.customer_email}
                      onChange={(e) => setNewBooking({ ...newBooking, customer_email: e.target.value })}
                      placeholder="email@exemplo.pt"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Especialidade</Label>
                    <Input
                      value={newBooking.specialty}
                      onChange={(e) => setNewBooking({ ...newBooking, specialty: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Profissional</Label>
                    <Input
                      value={newBooking.professional_name}
                      onChange={(e) => setNewBooking({ ...newBooking, professional_name: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Calendário</Label>
                    <Select
                      value={newBooking.calendar_id || "auto"}
                      onValueChange={(value) => setNewBooking({ ...newBooking, calendar_id: value === "auto" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automático</SelectItem>
                        {calendars.map((calendar) => (
                          <SelectItem key={calendar.id} value={calendar.id}>
                            {calendar.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Input
                      type="date"
                      value={newBooking.requested_date}
                      onChange={(e) => setNewBooking({ ...newBooking, requested_date: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Hora</Label>
                    <Input
                      type="time"
                      value={newBooking.requested_time}
                      onChange={(e) => setNewBooking({ ...newBooking, requested_time: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Duração</Label>
                    <Select
                      value={String(newBooking.duration_minutes)}
                      onValueChange={(value) => setNewBooking({ ...newBooking, duration_minutes: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 min</SelectItem>
                        <SelectItem value="45">45 min</SelectItem>
                        <SelectItem value="60">60 min</SelectItem>
                        <SelectItem value="90">90 min</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Método de pagamento</Label>
                    <Select
                      value={newBooking.payment_method}
                      onValueChange={(value) => setNewBooking({ ...newBooking, payment_method: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mbway">MB WAY</SelectItem>
                        <SelectItem value="multibanco">Multibanco</SelectItem>
                        <SelectItem value="cash">Dinheiro</SelectItem>
                        <SelectItem value="card">Cartão</SelectItem>
                        <SelectItem value="transfer">Transferência</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Valor (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newBooking.payment_amount}
                      onChange={(e) => setNewBooking({ ...newBooking, payment_amount: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Pré-pagamento obrigatório</Label>
                    <Select
                      value={newBooking.payment_required ? "yes" : "no"}
                      onValueChange={(value) => setNewBooking({ ...newBooking, payment_required: value === "yes" })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Sim</SelectItem>
                        <SelectItem value="no">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label>Notas</Label>
                    <Input
                      value={newBooking.notes}
                      onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })}
                      placeholder="Observações do pedido"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setBookingDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateConsultationBooking} disabled={creatingBooking}>
                    {creatingBooking ? "A criar..." : "Criar pedido"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Card className="border-2 border-border" data-testid="prepaid-panel">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold font-heading flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-green-600" />
                  Consultas Pré-pagas
                </CardTitle>
                <CardDescription>Consultas marcadas como PREPAGO</CardDescription>
              </CardHeader>
              <CardContent>
                {prepaidAppointments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Sem consultas pré-pagas
                  </p>
                ) : (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {prepaidAppointments.map((apt) => (
                        <div 
                          key={apt.id} 
                          className={`p-2 rounded-sm border text-sm ${apt.prepaid_confirmed ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}
                        >
                          <div className="flex items-start gap-2">
                            <Checkbox 
                              checked={apt.prepaid_confirmed}
                              onCheckedChange={(checked) => handleConfirmPrepaid(apt.id, checked)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate">{apt.patient_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(apt.appointment_date)}
                              </p>
                              {apt.professional && (
                                <p className="text-xs text-blue-600">{apt.professional}</p>
                              )}
                            </div>
                            {apt.prepaid_confirmed && (
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Calendars Card */}
            <Card className="border-2 border-border" data-testid="calendars-panel">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold font-heading flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Calendários
                  </CardTitle>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-primary hover:bg-[#FFF5B6] hover:text-primary" data-testid="add-calendar-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-calendar-dialog">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Adicionar Calendário</DialogTitle>
                        <DialogDescription>
                          Adicione um URL iCal do Google Calendar
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nome do Calendário</Label>
                          <Input
                            id="name"
                            placeholder="Ex: Calendário Principal"
                            value={newCalendar.name}
                            onChange={(e) => setNewCalendar({ ...newCalendar, name: e.target.value })}
                            data-testid="calendar-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="url">URL iCal</Label>
                          <Input
                            id="url"
                            placeholder="https://calendar.google.com/calendar/ical/..."
                            value={newCalendar.ical_url}
                            onChange={(e) => setNewCalendar({ ...newCalendar, ical_url: e.target.value })}
                            data-testid="calendar-url-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            No Google Calendar: Configurações → Calendário → Endereço secreto em formato iCal
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" data-testid="cancel-calendar-btn">Cancelar</Button>
                        </DialogClose>
                        <Button 
                          onClick={handleAddCalendar} 
                          disabled={addingCalendar}
                          className="bg-primary hover:bg-[#FFF5B6] hover:text-primary"
                          data-testid="save-calendar-btn"
                        >
                          {addingCalendar ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4 mr-2" />
                          )}
                          Adicionar
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {calendars.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground" data-testid="no-calendars">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Sem calendários</p>
                    <p className="text-sm">Adicione um calendário iCal</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {calendars.map((calendar) => (
                      <div 
                        key={calendar.id} 
                        className="calendar-card px-3 py-2 bg-muted/40 rounded-sm border border-border/60"
                        data-testid={`calendar-card-${calendar.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col md:flex-row md:items-center md:gap-3 min-w-0">
                              <p className="font-semibold text-sm truncate">{calendar.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                Última sync: {calendar.last_sync ? formatDate(calendar.last_sync) : "Nunca"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleSyncCalendar(calendar.id)}
                              disabled={syncing}
                              data-testid={`sync-calendar-btn-${calendar.id}`}
                              title="Sincronizar"
                            >
                              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteCalendar(calendar.id, calendar.name)}
                              data-testid={`delete-calendar-btn-${calendar.id}`}
                              title="Apagar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          </div>}
        </div>
      </main>

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-2xl" data-testid="message-detail-dialog">
          {selectedMessage && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {selectedMessage.patient_name}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {selectedMessage.patient_phone}
                  </span>
                  <MessageTypeBadge type={selectedMessage.message_type} />
                  <StatusBadge status={selectedMessage.status} />
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-sm">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Pré-visualização da Mensagem
                  </p>
                  <div className="message-preview bg-white text-slate-900 p-4 rounded-sm border shadow-sm whitespace-pre-wrap break-words">
                    {selectedMessage.message_text}
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Agendado para: {formatDate(selectedMessage.scheduled_for)}</span>
                  {selectedMessage.sent_at && (
                    <span>Enviado em: {formatDate(selectedMessage.sent_at)}</span>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                {(selectedMessage.status === "pending" || selectedMessage.status === "manual_pending") && (
                  <>
                    {selectedMessage.status === "manual_pending" && (
                      <Button
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        onClick={() => {
                          setSelectedMessages(new Set([selectedMessage.id]));
                          setActiveTab("manual_pending");
                          setSelectedMessage(null);
                        }}
                        data-testid="dialog-select-manual-btn"
                      >
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Selecionar para envio
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        openWhatsApp(selectedMessage.whatsapp_link);
                      }}
                      data-testid="dialog-whatsapp-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Abrir SMS
                    </Button>
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <AlertTitle className="text-blue-800">Estado protegido</AlertTitle>
                      <AlertDescription className="text-blue-700">
                        Para evitar erros, esta SMS não pode ser marcada manualmente como enviada ou falhada neste modal.
                      </AlertDescription>
                    </Alert>
                  </>
                )}
                {selectedMessage.status === "sent" && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Mensagem já enviada</AlertTitle>
                    <AlertDescription className="text-green-700">
                      Esta SMS está protegida e não deve voltar a pendente.
                    </AlertDescription>
                  </Alert>
                )}
                {selectedMessage.status === "failed" && (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">Falha protegida</AlertTitle>
                    <AlertDescription className="text-red-700">
                      Esta SMS falhou e não deve ser reenviada automaticamente sem revisão da lógica de segurança.
                    </AlertDescription>
                  </Alert>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default NotificationsBookingApp;
